import React from 'react';
import {
  isRouteErrorResponse,
  useRouteError,
  createBrowserRouter,
  RouteObject,
} from 'react-router-dom';

import {
  IndexView,
  HomePage,
  CourseOverview,
  CourseCatalogue,
  Contact,
  Faq,
  ProfileLearner,
  Dashboard,
  CourseWorking,
  AboutUs,
  BroughtToYou,
  ProfileGuide,
  CourseProgress,
  Certificate,
  PrivacyPolicy,
  TermsOfUse,
  Login,
  Registration,
  Logout,
  ForgotPassword,
  ResetPassword,
  UnsubscribeSuccess,
  UnsubscribeError,
  Cohorts,
  CohortCreate,
  MyCohorts,
  CohortLayout,
  CohortMembers,
  CohortInfo,
  CohortDiscussionsView,
  JoinCohort,
} from './views';
import {
  Footer,
  Header,
  PresentedBy,
  SsoLoginWrapper,
} from './components';
import type { AuthenticatedUser } from './types';

const ErrorPage = (): React.ReactElement | null => {
  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      window.location.href = '/';
    } else if (error.status === 403) {
      window.location.href = '/';
    } else if (error.status === 401) {
      localStorage.setItem('redirect-path', window.location.pathname);
      window.location.href = `/login?redirect-to=${window.location.href}`;
    }
  }
  return null;
};

const authCheckFn = (
  shouldBeAdmin: boolean,
  shouldBeLoggedIn: boolean,
  authenticatedUser: AuthenticatedUser | null,
) => () => {
  switch (true) {
    case !shouldBeLoggedIn:
      return null;
    case shouldBeAdmin && !authenticatedUser?.administrator:
      // @ts-ignore
      throw Response.json({ message: 'Not Admin' }, { status: 403 });
    case shouldBeLoggedIn && !authenticatedUser?.userId:
      // @ts-ignore
      throw Response.json({ message: 'Not Logged In' }, { status: 401 });
    default:
      return null;
  }
};

// Exported so a test can inspect a route's loader directly.
export const createRoutes = (authenticatedUser: AuthenticatedUser | null): RouteObject[] => [
  {
    path: '',
    element: <IndexView />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        element: (
          <HomePage>
            <Header />
            <Footer />
          </HomePage>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/course-overview/:courseID',
        element: (
          <CourseOverview>
            <Header />
            <Footer />
          </CourseOverview>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/course-catalogue',
        element: (
          <CourseCatalogue>
            <Header />
            <Footer />
          </CourseCatalogue>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/faq',
        element: (
          <Faq>
            <Header />
            <Footer />
          </Faq>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/contact',
        element: (
          <Contact>
            <Header />
            <Footer />
          </Contact>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/profile-learner',
        element: (
          <ProfileLearner>
            <Header />
            <Footer />
          </ProfileLearner>
        ),
        loader: authCheckFn(false, true, authenticatedUser),
      },
      {
        // Public per `portal/cohorts-navigation`: anonymous visitors get the page, not a login wall.
        path: '/cohorts',
        element: (
          <Cohorts>
            <Header />
            <Footer />
          </Cohorts>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/cohorts/create',
        element: (
          <CohortCreate>
            <Header />
            <Footer />
          </CohortCreate>
        ),
        loader: authCheckFn(false, true, authenticatedUser),
      },
      {
        path: '/cohorts/mine',
        element: (
          <MyCohorts>
            <Header />
            <Footer />
          </MyCohorts>
        ),
        loader: authCheckFn(false, true, authenticatedUser),
      },
      {
        // Placed after the static /cohorts/create and /cohorts/mine routes above
        // so this param route never shadows them.
        path: '/cohorts/:slug',
        element: (
          <CohortLayout>
            <Header />
            <Footer />
          </CohortLayout>
        ),
        loader: authCheckFn(false, true, authenticatedUser),
        children: [
          {
            index: true,
            element: <CohortMembers />,
          },
          {
            path: 'info',
            element: <CohortInfo />,
          },
          {
            path: 'discussions',
            element: <CohortDiscussionsView />,
          },
        ],
      },
      {
        path: '/groups/join/:slug',
        element: (
          <JoinCohort>
            <Header />
            <Footer />
          </JoinCohort>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/user-dashboard',
        element: (
          <Dashboard>
            <Header />
            <Footer />
          </Dashboard>
        ),
        loader: authCheckFn(false, true, authenticatedUser),
      },
      {
        path: '/course-working/:courseID/:subsectionId?/:unitID?',
        element: (
          <CourseWorking>
            <Header />
            <Footer />
          </CourseWorking>
        ),
        loader: authCheckFn(false, true, authenticatedUser),
      },
      {
        path: '/course-notes/:courseID',
        element: (
          <CourseProgress>
            <Header />
            <Footer />
          </CourseProgress>
        ),
        loader: authCheckFn(false, true, authenticatedUser),
      },
      {
        path: '/profile-guide/:learnerID',
        element: (
          <ProfileGuide>
            <Header />
            <Footer />
          </ProfileGuide>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/presented-by',
        element: (
          <PresentedBy>
            <Header />
            <Footer />
          </PresentedBy>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/privacy-policy',
        element: (
          <PrivacyPolicy>
            <Header />
            <Footer />
          </PrivacyPolicy>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/terms-of-use',
        element: (
          <TermsOfUse>
            <Header />
            <Footer />
          </TermsOfUse>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/course-overview',
        element: (
          <AboutUs>
            <Header />
            <Footer />
          </AboutUs>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/certificate/:credentialID',
        element: <Certificate />,
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/brought-to-you-by',
        element: (
          <BroughtToYou>
            <Header />
            <Footer />
          </BroughtToYou>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: 'sso-login',
        element: <SsoLoginWrapper />,
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/login',
        element: <Login><Header hideLogin /></Login>,
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/signup',
        element: <Registration><Header hideLogin /></Registration>,
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/logout',
        element: <Logout />,
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/forgot-password',
        element: <ForgotPassword><Header hideLogin /></ForgotPassword>,
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/password_reset_confirm/:params',
        element: <ResetPassword><Header hideLogin /></ResetPassword>,
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/notifications/unsubscribe/success',
        element: (
          <UnsubscribeSuccess>
            <Header />
            <Footer />
          </UnsubscribeSuccess>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
      {
        path: '/notifications/unsubscribe/error',
        element: (
          <UnsubscribeError>
            <Header />
            <Footer />
          </UnsubscribeError>
        ),
        loader: authCheckFn(false, false, authenticatedUser),
      },
    ],
  },
];

const routes = (authenticatedUser: AuthenticatedUser | null) => createBrowserRouter(createRoutes(authenticatedUser));

export default routes;
